import React from 'react';
// TODO update this to import from commmons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import I18n from 'i18n-js';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { RCAccordion } from '@reverbdotcom/cadence/components';
import { TruncationContainer } from '@reverbdotcom/commons/src/components/truncation_container';
import { SanitizedRender } from '@reverbdotcom/commons';
import { Item2Prop65Warning, Item2Prop65WarningFragment } from './item2_prop65_warning';
import { useIsMobileWindowWidth } from '@reverbdotcom/cadence/components/helpers/is-mobile';
import SpecList from '@reverbdotcom/commons/src/components/spec_list';
import { isCoreBrandNew } from '@reverbdotcom/commons/src/condition_helpers';
import { Item2FlagListing } from './item2_flag_listing';
import { Item2FlagListingMeData, Listing } from '@reverbdotcom/commons/src/gql/graphql';

interface IProps {
  me: Item2FlagListingMeData.Fragment;
  listing: Listing;
}

interface WrappedWithMobileAccordionProps {
  children: JSX.Element;
  id: string;
  heading: string | React.ReactNode;
  isMobile: boolean;
}

const EXPERIMENTAL_TRUNCATION_CONTAINER_REM_HEIGHT = 120;

function WrappedWithMobileAccordion(props: WrappedWithMobileAccordionProps): JSX.Element {
  const { isMobile, heading, id, children } = props;

  if (isMobile) {
    return (
      <RCAccordion
        heading={heading}
        defaultOpen
        id={id}
      >
        {children}
      </RCAccordion>
    );
  } else {
    return children;
  }
}

export const Item2AboutThisListingFragment = gql`
  fragment Item2AboutThisListingData on Listing {
    _id
    id
    description
    publishedAt {
      seconds
    }
    hasInventory
    condition {
      conditionUuid
    }
    specs {
      productSpecs {
        _id
        name
        value
        path
        slug
        type
      }
    }

    ...Item2Prop65WarningData
  }
  ${Item2Prop65WarningFragment}
`;

export function Item2AboutThisListing({ listing, me }: IProps) {
  const user = useUser();
  const isMobile = useIsMobileWindowWidth();

  if (!listing) return null;

  const hideDateListed = isCoreBrandNew(listing.condition.conditionUuid) || listing.hasInventory;
  const flagListingUserProps = Object.assign({}, user, me);

  return (
    <div className="item2__about-this-listing">
      <h2 className="item2__about-this-listing-heading">{I18n.t('discovery.item2Description.about')}</h2>

      <div className="item2__about-this-listing-inner">
        <div className="item2__about-description">
          <div className="item2-description">
            <WrappedWithMobileAccordion
              id="description"
              heading={I18n.t('discovery.item2Description.productDescription')}
              isMobile={isMobile}
            >
              <>
                <TruncationContainer
                  remMaxHeight={EXPERIMENTAL_TRUNCATION_CONTAINER_REM_HEIGHT}
                  trackingName="Item2Description"
                >
                  {!isMobile &&
                    <h2 className="item2__about-this-listing-subheading">{I18n.t('discovery.item2Description.productDescription')}</h2>
                  }

                  <SanitizedRender
                    html={listing.description}
                    htmlTag="div"
                    className="item2-description__content"
                    truncationHeight={null}
                  />
                </TruncationContainer>
                <Item2Prop65Warning
                  listing={listing}
                />
              </>
            </WrappedWithMobileAccordion>
          </div>
        </div>
        <div className="item2__about-specs">
          <WrappedWithMobileAccordion
            id="product-specs"
            heading={I18n.t('discovery.item2Description.specs')}
            isMobile={isMobile}
          >
            <>
              {!isMobile &&
                <h2 className="item2__about-this-listing-subheading">{I18n.t('discovery.item2Description.specs')}</h2>
              }

              <SpecList
                specs={listing.specs.productSpecs}
                dateListed={!hideDateListed && listing.publishedAt?.seconds}
                restyledTable
              />
            </>
          </WrappedWithMobileAccordion>
          <div className="item2__about-this-listing-report">
            <Item2FlagListing
              user={flagListingUserProps}
              listing={listing}
              id="item2_flag_listing"
              transparentButton
            />
          </div>
        </div>
      </div>
    </div>
  );
}
